import type { INodePoolsNameAndId } from "@/models/node-pool.model";
import type { ICustomHeaderRadioButtonsConfig } from "@/models/table.model";
import type { ISelectOption } from "@/models/global.model";
import { RankingMetricType, RankingOrderType, type Resources } from "@/swagger-models/org-unit-service-client";
import type {
  IClusterTotalResources,
  IDepartmentNodePoolResourcesSum,
  INodePoolAllocatedNonPreemptibleSum,
  INodePoolIsOverQuota,
  INodePoolResourcesSum,
} from "@/models/resource.model";
import { EQuotaEntity } from "@/models/resource.model";

export interface IEmptyProjectModelConfig {
  nodePoolsNamesAndIds: INodePoolsNameAndId[];
  clusterId: string;
  isCpuEnabled: boolean;
  parentId?: string;
  defaultNodePools?: string[];
}

export interface IEmptyDepartmentModelConfig {
  nodePoolsNamesAndIds: INodePoolsNameAndId[];
  clusterId: string;
  isCpuEnabled: boolean;
  defaultNodePools?: string[];
}
/** Over time data **/
export enum EOrgUnitOverTimeValue {
  TwentyFourHours = "24h",
  SevenDays = "7d",
  ThirtyDays = "30d",
}

export const ORG_UNIT_OVERTIME_CUSTOM_HEADER_CONFIG = {
  label: "Show the last",
  options: [
    { label: "24 hours", value: EOrgUnitOverTimeValue.TwentyFourHours, shortLabel: "24h" },
    { label: "7 days", value: EOrgUnitOverTimeValue.SevenDays, shortLabel: "7d" },
    { label: "30 days", value: EOrgUnitOverTimeValue.ThirtyDays, shortLabel: "30d" },
  ],
  iconName: "fa-regular fa-calendar-clock q-mr-sm text-black-30",
  chipPrefix: "Last",
} as Partial<ICustomHeaderRadioButtonsConfig>;

export enum EOrgUnitOverTimeColumnName {
  AverageGpuUtilization = "averageGpuUtilization",
  AverageGpuAllocation = "averageGpuAllocation",
}

/** Ranking **/
export enum EOrgUnitEntityRankingValue {
  Department = "Department",
  Project = "Project",
}

export enum EOrgUnitRankingMetricValue {
  TopAverageGpuAllocation = "topAverageGpuAllocation",
  TopAverageGpuUtilization = "topAverageGpuUtilization",
  BottomAverageGpuAllocation = "bottomAverageGpuAllocation",
  BottomAverageGpuUtilization = "bottomAverageGpuUtilization",
}

export interface IOrgUnitEntitySelectOption extends ISelectOption {
  value: EOrgUnitEntityRankingValue;
}

export const ORG_UNIT_RANKING_ENTITY_SELECT_OPTIONS: IOrgUnitEntitySelectOption[] = [
  { label: "Projects", value: EOrgUnitEntityRankingValue.Project },
  { label: "Departments", value: EOrgUnitEntityRankingValue.Department },
];

export interface IOrgUnitRankingMetricSelectOption extends ISelectOption {
  value: EOrgUnitRankingMetricValue;
}

export const ORG_UNIT_RANKING_METRIC_SELECT_OPTIONS: IOrgUnitRankingMetricSelectOption[] = [
  { label: "Highest Avg. GPU allocation", value: EOrgUnitRankingMetricValue.TopAverageGpuAllocation },
  { label: "Lowest Avg. GPU allocation", value: EOrgUnitRankingMetricValue.BottomAverageGpuAllocation },
  { label: "Highest  Avg. GPU utilization", value: EOrgUnitRankingMetricValue.TopAverageGpuUtilization },
  { label: "Lowest  Avg. GPU utilization", value: EOrgUnitRankingMetricValue.BottomAverageGpuUtilization },
];

export interface IOrgUnitTimeFrameSelectOption extends ISelectOption {
  value: EOrgUnitOverTimeValue;
}

export const ORG_UNIT_TIME_FRAME_SELECT_OPTIONS: IOrgUnitTimeFrameSelectOption[] = [
  { label: "Last 24h", value: EOrgUnitOverTimeValue.TwentyFourHours },
  { label: "Last 7d", value: EOrgUnitOverTimeValue.SevenDays },
  { label: "Last 30d", value: EOrgUnitOverTimeValue.ThirtyDays },
];

export const rankingMetricTypeMap: Record<
  EOrgUnitRankingMetricValue,
  Record<EOrgUnitOverTimeValue, RankingMetricType>
> = {
  [EOrgUnitRankingMetricValue.TopAverageGpuAllocation]: {
    [EOrgUnitOverTimeValue.TwentyFourHours]: RankingMetricType.AvgGpuAllocation24h,
    [EOrgUnitOverTimeValue.SevenDays]: RankingMetricType.AvgGpuAllocation7d,
    [EOrgUnitOverTimeValue.ThirtyDays]: RankingMetricType.AvgGpuAllocation30d,
  },
  [EOrgUnitRankingMetricValue.TopAverageGpuUtilization]: {
    [EOrgUnitOverTimeValue.TwentyFourHours]: RankingMetricType.AvgGpuUtilization24h,
    [EOrgUnitOverTimeValue.SevenDays]: RankingMetricType.AvgGpuUtilization7d,
    [EOrgUnitOverTimeValue.ThirtyDays]: RankingMetricType.AvgGpuUtilization30d,
  },
  [EOrgUnitRankingMetricValue.BottomAverageGpuAllocation]: {
    [EOrgUnitOverTimeValue.TwentyFourHours]: RankingMetricType.AvgGpuAllocation24h,
    [EOrgUnitOverTimeValue.SevenDays]: RankingMetricType.AvgGpuAllocation7d,
    [EOrgUnitOverTimeValue.ThirtyDays]: RankingMetricType.AvgGpuAllocation30d,
  },
  [EOrgUnitRankingMetricValue.BottomAverageGpuUtilization]: {
    [EOrgUnitOverTimeValue.TwentyFourHours]: RankingMetricType.AvgGpuUtilization24h,
    [EOrgUnitOverTimeValue.SevenDays]: RankingMetricType.AvgGpuUtilization7d,
    [EOrgUnitOverTimeValue.ThirtyDays]: RankingMetricType.AvgGpuUtilization30d,
  },
};

export const rankingOrderMap: Record<EOrgUnitRankingMetricValue, RankingOrderType> = {
  [EOrgUnitRankingMetricValue.TopAverageGpuAllocation]: RankingOrderType.Top,
  [EOrgUnitRankingMetricValue.TopAverageGpuUtilization]: RankingOrderType.Top,
  [EOrgUnitRankingMetricValue.BottomAverageGpuAllocation]: RankingOrderType.Bottom,
  [EOrgUnitRankingMetricValue.BottomAverageGpuUtilization]: RankingOrderType.Bottom,
};

/** Quota Management */
export enum EQuotaTab {
  Scheduling = "scheduling",
  Quota = "quota",
}

export enum EEntityPriorityValue {
  Lowest = "Lowest",
  VeryLow = "VeryLow",
  Low = "Low",
  MediumLow = "MediumLow",
  Medium = "Medium",
  MediumHigh = "MediumHigh",
  High = "High",
  VeryHigh = "VeryHigh",
  Highest = "Highest",
}

export enum EEntityPriorityLabel {
  Lowest = "Lowest",
  VeryLow = "Very Low",
  Low = "Low",
  MediumLow = "Medium Low",
  Medium = "Medium",
  MediumHigh = "Medium High",
  High = "High",
  VeryHigh = "Very High",
  Highest = "Highest",
}

export interface IEntityPrioritySelectOption extends ISelectOption {
  value: EEntityPriorityValue;
  label: EEntityPriorityLabel;
}

export const ENTITY_PRIORITY_SELECT_OPTIONS: IEntityPrioritySelectOption[] = [
  { label: EEntityPriorityLabel.Lowest, value: EEntityPriorityValue.Lowest },
  { label: EEntityPriorityLabel.VeryLow, value: EEntityPriorityValue.VeryLow },
  { label: EEntityPriorityLabel.Low, value: EEntityPriorityValue.Low },
  { label: EEntityPriorityLabel.MediumLow, value: EEntityPriorityValue.MediumLow },
  { label: EEntityPriorityLabel.Medium, value: EEntityPriorityValue.Medium },
  { label: EEntityPriorityLabel.MediumHigh, value: EEntityPriorityValue.MediumHigh },
  { label: EEntityPriorityLabel.High, value: EEntityPriorityValue.High },
  { label: EEntityPriorityLabel.VeryHigh, value: EEntityPriorityValue.VeryHigh },
  { label: EEntityPriorityLabel.Highest, value: EEntityPriorityValue.Highest },
];

export interface NodePoolOptions {
  readOnly: boolean;
  isCpuEnabled: boolean;
  isOnlyDefaultNodePool: boolean;
  isOverQuotaPriorityEnabled: boolean;
  selectedTab: EQuotaTab;
  isClusterSupportsPriority: boolean;
}

export interface NodePoolModel {
  resource: Resources;
  priorities: string[];
  isNodePoolOverQuota: INodePoolIsOverQuota;
  projectsDeservedResources: INodePoolResourcesSum;
  departmentDeservedResources: IDepartmentNodePoolResourcesSum;
  allocatedNonPreemptibleResources: INodePoolAllocatedNonPreemptibleSum;
  totalClusterResources: IClusterTotalResources;
}

export const quotaManagementSectionTitle: Record<EQuotaEntity, string> = {
  [EQuotaEntity.project]:
    "Drag the node pools to set the order of priority by which workloads will be scheduled to use them. Then click the boxes to set the quotas and scheduling preferences for each node pool.",
  [EQuotaEntity.department]:
    "Drag the node pools to set the priority order in which workloads use them. Subordinate projects inherit this unless overridden at the project level. Then click the boxes to set the quotas and other scheduling preferences for each node pool.",
};
